import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Route as RootRoute } from './__root';

interface MeResponse {
  id?: number;
  nick?: string;
  email?: string;
  [key: string]: unknown;
}

function HealthPage() {
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => api.get<MeResponse>('/v1/auth/me', { signal }),
    retry: false,
  });

  return (
    <div className="mx-auto max-w-2xl px-8 py-10 font-mono text-[12px]">
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Phase 0 · /health
      </div>
      <h1 className="mt-2 font-display text-3xl leading-none tracking-tight">
        Bearer bridge healthcheck.
      </h1>

      <dl className="mt-8 grid grid-cols-[140px_1fr] gap-y-2 border border-line bg-paper p-4 text-[12px]">
        <dt className="text-ink-3">endpoint</dt>
        <dd className="text-ink">GET /api/v1/auth/me</dd>

        <dt className="text-ink-3">state</dt>
        <dd className="text-ink">
          {isLoading
            ? 'loading…'
            : error
              ? 'error'
              : data
                ? 'ok'
                : 'idle'}
          {isFetching && !isLoading ? ' · refreshing' : ''}
        </dd>

        {data ? (
          <>
            <dt className="text-ink-3">user</dt>
            <dd className="text-ink">
              {data.nick ?? '—'}
              {data.id ? ` · #${data.id}` : ''}
            </dd>
          </>
        ) : null}

        {error ? (
          <>
            <dt className="text-ink-3">status</dt>
            <dd className="text-ink">
              {error instanceof ApiError ? error.status : '—'}
            </dd>
            <dt className="text-ink-3">message</dt>
            <dd className="text-warn">
              {error instanceof Error ? error.message : String(error)}
            </dd>
          </>
        ) : null}
      </dl>

      <div
        data-testid="health-status"
        className="mt-6 inline-flex items-center gap-2 border border-line bg-paper px-3 py-1.5 text-[11px] uppercase tracking-[0.08em]"
      >
        <span
          className="inline-block size-2"
          style={{
            background: data ? 'var(--ok)' : error ? 'var(--warn)' : 'var(--ink-3)',
          }}
        />
        {data ? 'OK' : error ? 'FAIL' : 'CHECKING'}
      </div>
    </div>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/health',
  component: HealthPage,
});
